import React from 'react';
import classnames from 'classnames';
import {  useTranslation } from 'react-i18next';
import { DATE_FORMAT_DD_MM_YYYY } from '@/helpers/common';
import { prettyDate } from '@/helpers/utils';
import { useCancelWithdrawRequest } from '@/query/queries/cash/useCancelWithdrawRequest';
import { useGetWithdrawHistory } from '@/query/queries/cash/useGetWithdrawHistory';
import { Badge, Pagination, Table } from '@scramble/ui';
import { prettyMoney } from '@scramble/utils';
import { EmptyState } from './empty';
import IconCancel from '@scramble/design-system/icons/trash-01.svg';
import s from './index.module.scss';

const REQUEST_STATUSES = t => ({
  new: { id: 'new', title: t('withdraw:request_status.new'), type: 'info' },
  in_process: {
    id: 'in_process',
    title: t('withdraw:request_status.in_process'),
    type: 'info'
  },
  completed: { id: 'completed', title: t('withdraw:request_status.processed'), type: 'success' },
  cancelled: { id: 'cancelled', title: t('withdraw:request_status.cancelled'), type: 'danger' },
  refund: { id: 'refund', title: t('withdraw:request_status.refund'), type: 'warning' },
  rejected_from_lhv: {
    id: 'rejected_from_lhv',
    title: t('withdraw:request_status.rejected_by_bank'),
    type: 'danger',
  },
});

export const WithdrawHistory = ({ className }) => {
  const [params, setParams] = React.useState({
    page: 1,
    limit: 10,
  });
  const { mutateAsync: cancelWithdrawRequest } = useCancelWithdrawRequest();
  const { data, refetch, isLoading } = useGetWithdrawHistory(params);
  const { t } = useTranslation();
  const REQUEST_STATUSES_T = REQUEST_STATUSES(t);

  const handleCancel = async (requestId) => {
    const res = await cancelWithdrawRequest(requestId);
    if (res?.success) refetch();
  }

  const hasData = !!data?.total_count;

  if (isLoading) return null;

  return (
    <div className={classnames(s.history, className)}>
      <h2 className="f-headline-xs f-500">
        {t('withdraw:withdrawals_history.title')}
      </h2>

      {hasData ? (
        <>
          <Table className={s.table} minWidth={688}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('withdraw:withdrawals_history.date')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('withdraw:withdrawals_history.amount')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('withdraw:withdrawals_history.destination')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('withdraw:withdrawals_history.status')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('withdraw:withdrawals_history.action')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.list.map(item => (
                <Table.Row key={item.id}>
                  <Table.Cell>
                    {prettyDate(item.created_at, DATE_FORMAT_DD_MM_YYYY)}
                  </Table.Cell>
                  <Table.Cell>{prettyMoney(item.amount)}</Table.Cell>
                  <Table.Cell>
                    {item.external_account.account_number}
                  </Table.Cell>
                  <Table.Cell className={s.status}>
                    <Badge
                      className={s.badge}
                      type={REQUEST_STATUSES_T[item.status]?.type}
                      label={
                        REQUEST_STATUSES_T[item.status]?.title ?? item.status
                      }
                      dot
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {item.status === REQUEST_STATUSES_T.new.id && (
                      <button
                        type="button"
                        className={s.btn_cancel}
                        onClick={() => handleCancel(item.id)}
                      >
                        <img
                          src={IconCancel}
                          alt="cancel"
                          width={20}
                          height={20}
                        />
                      </button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Pagination
            currentPage={params.page}
            totalPages={data.total_pages}
            onPageChange={page => setParams(prev => ({ ...prev, page }))}
          />
        </>
      ) : (
        <EmptyState />
      )}
    </div>
  );
};